"use client";

import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

type SignInVisibilityContextValue = {
  hideSignIn: boolean;
  setHideSignIn: (hide: boolean) => void;
};

const SignInVisibilityContext = createContext<SignInVisibilityContextValue>({
  hideSignIn: false,
  setHideSignIn: () => {},
});

export function SignInVisibilityProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [hideSignIn, setHideSignIn] = useState(false);
  const value = useMemo(() => ({ hideSignIn, setHideSignIn }), [hideSignIn]);

  return (
    <SignInVisibilityContext.Provider value={value}>
      {children}
    </SignInVisibilityContext.Provider>
  );
}

export function useSignInVisibility() {
  return useContext(SignInVisibilityContext);
}

export function SignInVisibility({ hide }: { hide: boolean }) {
  const { setHideSignIn } = useSignInVisibility();

  // Layout effect: sync before paint so Sign In does not flash. (A passive effect paints
  // one frame too late; setState-during-render on the provider warns about updating a
  // parent while rendering a child.)
  useLayoutEffect(() => {
    setHideSignIn(hide);
    return () => setHideSignIn(false);
  }, [hide, setHideSignIn]);

  return null;
}
